import React, { useRef, useEffect, useState, useCallback, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  LogIn,
  LogOut,
  Moon,
  Palette,
  Sun,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUIStore } from '../../stores/uiStore';
import type { UserRole } from '../../types';
import { NAV_SECTIONS, filterSectionsByRole, isNavItemActive } from './navigation.config';

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { sidebarCollapsed: isCollapsed, toggleSidebar, setThemeEditorOpen, theme, toggleTheme } = useUIStore();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const [profileDropdownStyle, setProfileDropdownStyle] = useState<CSSProperties>({});

  const filteredSections = filterSectionsByRole(NAV_SECTIONS, user?.role as UserRole | undefined);

  const userAvatarUrl = (user as (typeof user & { avatar_url?: string; photo_url?: string; image_url?: string }) | null)?.avatar_url
    ?? (user as (typeof user & { avatar_url?: string; photo_url?: string; image_url?: string }) | null)?.photo_url
    ?? (user as (typeof user & { avatar_url?: string; photo_url?: string; image_url?: string }) | null)?.image_url
    ?? null;
  const userInitial = (user?.username?.[0] ?? 'U').toUpperCase();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const outsideProfile = !profileRef.current || !profileRef.current.contains(target);
      const outsideDropdown = !profileDropdownRef.current || !profileDropdownRef.current.contains(target);
      if (outsideProfile && outsideDropdown) setProfileOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Sidebar has overflow:hidden (clips content to its own width), so a
  // normally-nested dropdown gets cut off — worse the narrower the sidebar
  // is (collapsed vs. expanded). Portal it to <body> instead (same trick
  // the tooltip below already uses) and position it with fixed coordinates.
  const updateProfileDropdownPosition = useCallback(() => {
    const rect = profileRef.current?.getBoundingClientRect();
    if (!rect) return;
    setProfileDropdownStyle({
      position: 'fixed',
      top: 'auto',
      right: 'auto',
      left: rect.left,
      bottom: window.innerHeight - rect.top + 6,
    });
  }, []);

  useEffect(() => {
    if (!profileOpen) return;
    window.addEventListener('resize', updateProfileDropdownPosition);
    return () => window.removeEventListener('resize', updateProfileDropdownPosition);
  }, [profileOpen, updateProfileDropdownPosition]);

  const toggleProfileOpen = () => {
    if (!profileOpen) updateProfileDropdownPosition();
    setProfileOpen((v) => !v);
  };

  useEffect(() => {
    if (!profileOpen) return;
    // Recompute after the sidebar's width transition (0.25s) finishes.
    const t = setTimeout(updateProfileDropdownPosition, 260);
    return () => clearTimeout(t);
  }, [isCollapsed, profileOpen, updateProfileDropdownPosition]);

  const navLinksRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({ opacity: 0 });

  const updateIndicator = useCallback((target: HTMLElement | null) => {
    if (!target || !navLinksRef.current) {
      setIndicatorStyle((s) => ({ ...s, opacity: 0 }));
      return;
    }
    const containerRect = navLinksRef.current.getBoundingClientRect();
    const linkRect = target.getBoundingClientRect();
    const top = linkRect.top - containerRect.top + navLinksRef.current.scrollTop;
    const height = linkRect.height;
    setIndicatorStyle({
      opacity: 1,
      top: top + height * 0.2,
      height: height * 0.55,
    });
  }, []);

  useEffect(() => {
    if (!navLinksRef.current) return;
    const activeEl = navLinksRef.current.querySelector('.nav-link.active') as HTMLElement | null;
    updateIndicator(activeEl);
  }, [location.pathname, isCollapsed, updateIndicator]);

  const [tooltip, setTooltip] = useState<{ text: string; style: CSSProperties } | null>(null);

  const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>, label?: string) => {
    updateIndicator(e.currentTarget);
    if (isCollapsed && label) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltip({
        text: label,
        style: { top: rect.top + rect.height / 2 },
      });
    }
  };

  const handleMouseLeave = () => {
    if (!navLinksRef.current) return;
    const activeEl = navLinksRef.current.querySelector('.nav-link.active') as HTMLElement | null;
    updateIndicator(activeEl);
    setTooltip(null);
  };

  const showTooltip = (e: React.MouseEvent<HTMLElement>, text: string) => {
    if (!isCollapsed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ text, style: { top: rect.top + rect.height / 2 } });
  };

  const hideTooltip = () => setTooltip(null);

  return (
    <nav className={`main-nav ${isCollapsed ? 'collapsed' : ''}`}>
      <Link to="/" className="nav-brand-link" title="Нүүр">
        <div className="nav-brand">
          <img src="/logo-v2.png" alt="TenderViz" className="nav-brand-logo" />
          <span><strong>Tender</strong>Viz</span>
        </div>
      </Link>

      <div className="nav-links" ref={navLinksRef}>
        {!isCollapsed && <div className="nav-menu-header">Цэс</div>}
        <div className="nav-indicator" style={indicatorStyle} />
        {filteredSections.map((section) => (
          <React.Fragment key={section.id}>
            {!isCollapsed && <div className="nav-section-label">{section.label}</div>}
            {section.items.map((item) => {
              const isActive = isNavItemActive(location.pathname, item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${isActive ? 'active' : ''}`}
                  onMouseEnter={(e) => handleMouseEnter(e, item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  <item.icon size={18} />
                  {!isCollapsed && <span>{item.label}</span>}
                </Link>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      <div className="nav-bottom">
        {/* top to bottom: light / dark, the design editor (admin), then the profile */}
        <button
          type="button"
          className="nav-link nav-bottom-link"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Цайвар горим' : 'Бараан горим'}
          onMouseEnter={(e) => isCollapsed && showTooltip(e, theme === 'dark' ? 'Цайвар горим' : 'Бараан горим')}
          onMouseLeave={hideTooltip}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          {!isCollapsed && <span>{theme === 'dark' ? 'Цайвар горим' : 'Бараан горим'}</span>}
        </button>
        {user?.role === 'admin' && (
          <button
            type="button"
            className="nav-link nav-bottom-link"
            onClick={() => setThemeEditorOpen(true)}
            aria-label="Дизайн"
            onMouseEnter={(e) => isCollapsed && showTooltip(e, 'Дизайн')}
            onMouseLeave={hideTooltip}
          >
            <Palette size={18} />
            {!isCollapsed && <span>Дизайн</span>}
          </button>
        )}
        <div className="nav-bottom-row nav-profile-wrap" ref={profileRef}>
          {user ? (
            <button
              type="button"
              className="nav-user"
              onClick={toggleProfileOpen}
              onMouseEnter={(e) => showTooltip(e, user.username)}
              onMouseLeave={hideTooltip}
            >
              {userAvatarUrl ? (
                <img src={userAvatarUrl} alt={user.username} className="nav-user-avatar" style={{ objectFit: 'cover' }} />
              ) : (
                <span className="nav-user-avatar">{userInitial}</span>
              )}
              {!isCollapsed && (
                <span className="nav-user-info">
                  <span className="nav-user-name">{user.username}</span>
                </span>
              )}
            </button>
          ) : (
            <Link
              to="/login"
              className="nav-user"
              onMouseEnter={(e) => showTooltip(e, 'Нэвтрэх')}
              onMouseLeave={hideTooltip}
            >
              <span className="nav-user-avatar"><LogIn size={15} /></span>
              {!isCollapsed && <span className="nav-user-info"><span className="nav-user-name">Нэвтрэх</span></span>}
            </Link>
          )}

          {user && profileOpen && createPortal(
            <div
              ref={profileDropdownRef}
              className="top-header-dropdown top-header-profile-dropdown nav-profile-dropdown"
              style={profileDropdownStyle}
            >
              <div className="top-header-profile-head">
                {userAvatarUrl ? (
                  <img src={userAvatarUrl} alt={user.username} className="top-header-profile-avatar" />
                ) : (
                  <span className="top-header-profile-avatar top-header-avatar-fallback">{userInitial}</span>
                )}
                <div>
                  <div className="top-header-profile-name">{user.username}</div>
                  <button className="top-header-profile-link" onClick={() => { setProfileOpen(false); navigate('/profile'); }}>
                    Профайл харах
                  </button>
                </div>
              </div>
              <button className="top-header-profile-row" onClick={() => { setProfileOpen(false); navigate('/settings'); }}>
                <span>Тохиргоо ба нууцлал</span>
                <ChevronRight size={14} />
              </button>
              <button className="top-header-profile-row" onClick={() => { setProfileOpen(false); navigate('/help'); }}>
                <span>Тусламж, дэмжлэг</span>
                <ChevronRight size={14} />
              </button>
              <button
                className="top-header-profile-row top-header-dropdown-item-danger"
                onClick={() => {
                  setProfileOpen(false);
                  logout();
                  navigate('/login');
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <LogOut size={14} />
                  Гарах
                </span>
              </button>
            </div>,
            document.body
          )}

        </div>
        <button
          className="nav-toggle-btn"
          onClick={toggleSidebar}
          onMouseEnter={(e) => showTooltip(e, isCollapsed ? 'Нээх' : 'Хураах')}
          onMouseLeave={hideTooltip}
        >
          {isCollapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
        </button>
      </div>

      {tooltip && createPortal(
        <div className="nav-tooltip" style={tooltip.style}>
          <div className="nav-tooltip-arrow" />
          {tooltip.text}
        </div>,
        document.body
      )}
    </nav>
  );
};

export default Navigation;
